/**
 * 쿼리 실행 모니터링 — 화면 쪽 순수 함수(ExecMonitorModal · 백엔드 database/execMonitor.js).
 */

/** 창이 들고 있는 스냅샷 수 — 넘으면 오래된 것부터 버린다(3초 간격이면 15분치) */
export const MAX_SNAPSHOTS = 300;

/** DBMS_XPLAN 표의 줄에서 Id 를 읽는다 — `|*  3 |` · `|   0 |`. 표의 줄이 아니면 null */
export function planLineId(line) {
  const m = /^\|\s*\*?\s*(\d+)\s*\|/.exec(String(line || ''));
  return m ? Number(m[1]) : null;
}

export const fmtCount = (n) => (n == null ? '-' : Number(n).toLocaleString());

export function fmtBytes(n) {
  if (n == null) return '-';
  const v = Number(n);
  if (v >= 1073741824) return `${(v / 1073741824).toFixed(1)}GB`;
  if (v >= 1048576) return `${(v / 1048576).toFixed(1)}MB`;
  if (v >= 1024) return `${Math.round(v / 1024)}KB`;
  return `${v}B`;
}

export const fmtSec = (n) => (n == null ? '-' : (Number(n) >= 100 ? `${Math.round(Number(n))}` : `${Math.round(Number(n) * 10) / 10}`));

/**
 * 합계 그래프의 지표. `kind` 가 값의 성질이다 — 섞어 그리면 안 된다:
 *   level       그 시점의 값 그대로(경과 시간 · PGA · TEMP)
 *   cumulative  세션 누적(접속 이후) → **첫 스냅샷과의 차이**로 그린다(모니터링을 시작한 뒤의 증가분)
 */
export const TOTAL_METRICS = [
  { id: 'elapsedSec', kind: 'level', unit: 'sec' },
  { id: 'logicalReads', kind: 'cumulative', unit: 'count', monitor: 'bufferGets' },
  { id: 'physicalReads', kind: 'cumulative', unit: 'count', monitor: 'diskReads' },
  { id: 'cpuSec', kind: 'cumulative', unit: 'sec', monitor: 'cpuSec' },
  { id: 'pgaBytes', kind: 'level', unit: 'bytes' },
  { id: 'tempBytes', kind: 'level', unit: 'bytes' },
];

export function fmtMetric(unit, v) {
  if (v == null) return '-';
  if (unit === 'bytes') return fmtBytes(v);
  if (unit === 'sec') return fmtSec(v);
  return fmtCount(Math.round(v));
}

const execKey = (snap) => (snap && snap.exec ? `${snap.exec.sqlId}|${snap.exec.execId}|${snap.exec.execStart}` : '');

/**
 * 스냅샷들 → 지표별 계열. **순수 함수**.
 * @returns {{ [id]: { points:(number|null)[], basis:'level'|'session'|'monitor', last, max } }}
 *   · 못 읽은 시점은 null 이다 — 0 으로 메우지 않는다(선이 끊긴다).
 *   · 누적 지표는 한 계열에 **한 소스만** 쓴다: SQL Monitor 를 켜서 문장 합계가 처음부터 끝까지 있으면 그 값(`monitor` — 이 실행 전체),
 *     아니면 세션 통계의 첫 스냅샷 대비 증가분(`session` — 모니터링을 시작한 뒤). 둘을 한 선에 섞지 않는다.
 *   · 실행이 바뀌면(같은 세션의 다음 문장) 기준을 다시 잡는다.
 */
export function seriesOf(snapshots) {
  const list = Array.isArray(snapshots) ? snapshots : [];
  const out = {};
  for (const m of TOTAL_METRICS) {
    let basis = m.kind === 'level' ? 'level' : 'session';
    if (m.kind === 'cumulative' && m.monitor && list.length
      && list.every(sn => sn && sn.stmt && sn.stmt[m.monitor] != null)) basis = 'monitor';
    let base = null; let baseKey = null;
    const points = list.map((sn) => {
      if (basis === 'monitor') return Number(sn.stmt[m.monitor]);
      const v = sn && sn.totals ? sn.totals[m.id] : null;
      if (v == null) return null;
      if (basis === 'level') return Number(v);
      const k = execKey(sn);
      if (base == null || k !== baseKey) { base = Number(v); baseKey = k; }
      return Math.max(0, Number(v) - base);
    });
    const nums = points.filter(v => v != null);
    out[m.id] = { points, basis, last: nums.length ? nums[nums.length - 1] : null, max: nums.length ? Math.max(...nums) : null };
  }
  return out;
}

/** 작은 선 그래프의 길 — 값이 없는 시점에서 선을 끊는다. 점이 하나면 점 하나짜리 길 */
export function sparkPath(points, w, h, pad = 2) {
  const n = points.length;
  const nums = points.filter(v => v != null);
  if (!nums.length) return '';
  const max = Math.max(...nums, 0); const min = Math.min(...nums, 0);
  const span = max - min || 1;
  const x = (i) => (n <= 1 ? w / 2 : pad + (i * (w - pad * 2)) / (n - 1));
  const y = (v) => h - pad - ((v - min) * (h - pad * 2)) / span;
  let d = ''; let pen = false;
  points.forEach((v, i) => {
    if (v == null) { pen = false; return; }
    d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    pen = true;
  });
  return d;
}

/**
 * 이 스냅샷에서 **못 얻은 것과 그 까닭** — 빈 칸이 「아무 일도 없다」 로 읽히면 안 된다.
 * @returns [{ key, params }] — i18n 키(common:emon.note.*)
 */
export function noteKeys(snap, prefs = {}) {
  const out = [];
  const n = (snap && snap.notes) || {};
  const add = (code, params) => out.push({ key: `common:emon.note.${code}`, params });
  if (n.session === 'NOT_ORACLE') add('notOracle');
  else if (n.session === 'NO_SESSION_KEY' || n.session === 'NO_PRIVILEGE') add('noSessionView');
  else if (n.session === 'SESSION_GONE') add('sessionGone');
  else if (n.session === 'AMBIGUOUS_INSTANCE') add('ambiguousInstance');
  else if (n.session && n.session !== 'IDLE') add('failed', { what: 'V$SESSION', reason: n.session });
  if (n.exec === 'NO_EXEC_ID') add('noExecId');
  if (n.plan === 'REMOTE_INSTANCE') add('remotePlan');
  else if (n.plan) add('failed', { what: 'DBMS_XPLAN', reason: n.plan });
  if (n.longops) add('failed', { what: 'V$SESSION_LONGOPS', reason: n.longops });
  if (n.monitor === 'NOT_MONITORED') add('notMonitored');
  else if (n.monitor === 'NO_PRIVILEGE') add('noPrivilege', { what: 'SQL Monitor' });
  else if (n.monitor) add('failed', { what: 'SQL Monitor', reason: n.monitor });
  if (n.totals === 'NO_PRIVILEGE') add('noPrivilege', { what: 'V$SESSTAT' });
  else if (n.totals) add('failed', { what: 'V$SESSTAT', reason: n.totals });
  if (snap && snap.totals && snap.totals.pxSessions > 0) add('pxQcOnly', { count: snap.totals.pxSessions });
  if (n.ash === 'NO_PRIVILEGE') add('noPrivilege', { what: 'ASH' });
  else if (n.ash) add('failed', { what: 'ASH', reason: n.ash });
  // 팩을 켜지 않았고 긴 작업도 없으면 줄 표시가 비어 있다 — 왜 비었는지 말한다
  const hasLine = Object.keys((snap && snap.lines) || {}).length > 0;
  if (snap && snap.running && !hasLine && !prefs.monitor && !prefs.ash && !n.exec) add('onlyLongops');
  return out;
}

/**
 * 결과 탭의 「실행계획(실제)」 · 「SQL Monitor 리포트」 단추 상태. **순수 함수**.
 * @param tab      결과 탭 { sessionId, sourceSql, query, isExecuting }
 * @param sessions 세션 목록(폴링) — { sessionId, dbType, statisticsLevel }
 * @returns null(단추를 그리지 않는다 — 세션에 묶이지 않았거나 Oracle 이 아니다) |
 *          { actual: { enabled, reason }, report: { enabled } }
 *   · 실제 계획은 줄별 실측이 쌓였을 때만 뜻이 있다: 세션이 STATISTICS_LEVEL=ALL 이거나 문장에 GATHER_PLAN_STATISTICS 힌트가 있을 때.
 *   · 실행 중에는 둘 다 끈다(실행 중인 문장은 「모니터링」 이 본다).
 */
export function lastExecButtons(tab, sessions) {
  if (!tab || !tab.sessionId) return null;
  const sess = (sessions || []).find(x => x.sessionId === tab.sessionId);
  if (!sess || sess.dbType !== 'oracle') return null;
  const sql = String(tab.sourceSql || tab.query || '');
  const hinted = /\/\*\+[^*]*GATHER_PLAN_STATISTICS/i.test(sql);
  const all = String(sess.statisticsLevel || '').toUpperCase() === 'ALL';
  const idle = !tab.isExecuting;
  return {
    actual: { enabled: idle && (all || hinted), reason: !idle ? 'running' : (all ? 'all' : (hinted ? 'hint' : 'off')) },
    report: { enabled: idle },
  };
}

/**
 * DBMS_XPLAN 글을 나눈다(2026-10-26 — 실행계획만 펼치고 나머지는 접는다):
 *   head  — 계획 표 앞(SQL_ID · SQL 문 · Plan hash value)
 *   table — 계획 표(구분선 · 머리 · 줄)
 *   tail  — 표 뒤의 절들 [{ title, lines }] (Predicate Information · Hint Report · Note …). 제목 다음 줄이 ---- 인 것을 절로 본다
 * 표를 못 찾으면 전부 table 로 둔다(원문을 그대로 보인다).
 */
export function splitPlanSections(planText) {
  const lines = String(planText || '').split('\n');
  const isDash = (l) => /^-{5,}\s*$/.test(l || '');
  const first = lines.findIndex(l => /^\|/.test(l));
  if (first < 0) return { head: [], table: lines, tail: [] };
  const start = first > 0 && isDash(lines[first - 1]) ? first - 1 : first;
  let end = first;
  while (end + 1 < lines.length && (/^\|/.test(lines[end + 1]) || isDash(lines[end + 1]))) end += 1;
  const head = lines.slice(0, start);
  while (head.length && !head[head.length - 1].trim()) head.pop();
  const rest = lines.slice(end + 1);
  const tail = [];
  let cur = null;
  rest.forEach((l, i) => {
    if (l.trim() && isDash(rest[i + 1]) && !isDash(l)) { cur = { title: l.trim(), lines: [] }; tail.push(cur); return; }
    if (!cur) { if (l.trim()) { cur = { title: '', lines: [l] }; tail.push(cur); } return; }
    if (isDash(l) && !cur.lines.length) return;   // 제목 밑줄
    cur.lines.push(l);
  });
  tail.forEach(sec => { while (sec.lines.length && !sec.lines[sec.lines.length - 1].trim()) sec.lines.pop(); });
  return { head, table: lines.slice(start, end + 1), tail: tail.filter(sec => sec.title || sec.lines.some(l => l.trim())) };
}

/**
 * 계획 표 오른쪽에 붙이는 **줄별 실측 칸**(SQL Monitor — 켤 때만, Tuning Pack). 실행 중인 문장의 줄별 논리 읽기는 Oracle 이 주지 않는다
 * (V$SQL_PLAN_STATISTICS 는 실행이 끝나야 쌓인다) — 그래서 칸은 실제 행 수 · 시작 횟수 · 물리 읽기 · 메모리 · TEMP 다.
 */
export const LINE_COLUMNS = [
  { key: 'outputRows', head: 'A-Rows', fmt: (v) => fmtCount(v) },
  { key: 'starts', head: 'Starts', fmt: (v) => fmtCount(v) },
  { key: 'readReqs', head: 'Reads', fmt: (v) => fmtCount(v) },
  { key: 'readBytes', head: 'Read', fmt: (v) => (v ? fmtBytes(v) : '-') },
  { key: 'workareaMem', head: 'Mem', fmt: (v) => (v ? fmtBytes(v) : '-') },
  { key: 'tempBytes', head: 'Temp', fmt: (v) => (v ? fmtBytes(v) : '-') },
];
const COL_W = 9;
const padL = (s, w) => (String(s).length >= w ? String(s) : ' '.repeat(w - String(s).length) + s);

/**
 * 칸마다의 폭 — 기본 COL_W, 값이 더 길면(천만 단위 A-Rows 「12,345,678」 등) **모든 스냅샷의 값 중 가장 긴 것**에 맞춘다(2026-11-04).
 * 고정 폭이면 긴 값이 그 줄만 밀어 세로 줄이 삐뚤어졌다. 모든 스냅샷을 보므로 눈금을 옮겨도 폭이 바뀌지 않는다.
 */
export function columnWidths(snapshots) {
  const w = {};
  LINE_COLUMNS.forEach((c) => { w[c.key] = Math.max(COL_W, c.head.length); });
  (snapshots || []).forEach((sn) => {
    Object.values((sn && sn.lines) || {}).forEach((l) => {
      const m = l && l.monitor;
      if (!m || !(Number(m.starts) > 0)) return;
      LINE_COLUMNS.forEach((c) => { if (m[c.key] != null) w[c.key] = Math.max(w[c.key], String(c.fmt(m[c.key])).length); });
    });
  });
  return w;
}

/** 계획 표의 한 줄 뒤에 붙일 글 — 구분선이면 ----, 머리면 칸 이름, 계획 줄이면 그 줄의 SQL Monitor 값. widths 는 columnWidths() */
export function lineColumnsText(line, monitor, widths) {
  const l = String(line || '');
  const wOf = (c) => (widths && widths[c.key]) || COL_W;
  if (/^-{5,}/.test(l)) return '-'.repeat(LINE_COLUMNS.reduce((a, c) => a + wOf(c) + 3, 0));
  if (/^\|/.test(l) && /\bOperation\b/.test(l)) return LINE_COLUMNS.map(c => ` ${padL(c.head, wOf(c))} |`).join('');
  if (planLineId(l) == null) return '';
  // **아직 시작하지 않은 줄(STARTS=0)은 빈 칸**(2026-10-26) — 「0행 · 0회 시작」 이 계획 전체를 덮어 정작 도는 줄이 묻혔다.
  // 시작했지만 아직 내보낸 행이 없는 줄(정렬·해시 조인의 빌드 중)은 0 을 그대로 보인다 — 그것은 뜻이 있다
  const started = !!(monitor && Number(monitor.starts) > 0);
  return LINE_COLUMNS.map(c => ` ${padL(started && monitor[c.key] != null ? c.fmt(monitor[c.key]) : '', wOf(c))} |`).join('');
}

/** 그 줄이 SQL Monitor 상 한 번이라도 시작했는가(STARTS>0) — 시작 안 한 줄은 강조하지 않는다 */
export const lineStarted = (l) => !!(l && l.monitor && Number(l.monitor.starts) > 0);
